import React from 'react'
import { useRouteMatch } from 'react-router-dom'

import StickyCommentInput from '../StickyCommentInput'

import { WarningText } from './AnnotationCommentBox.styles'
import { Banner, Link } from '@sketch/components'
import { useIsAnnotationPreview } from '../../context'
import { routes, usePreviewPwd } from '../../../routes'
import type { CommentInputProps } from '../CommentInput'

interface AnnotationCommentBoxProps
  extends Omit<
    CommentInputProps,
    'banner' | 'disableMentions' | 'mentionableUsersSource'
  > {
  showOlderVersionWarning: boolean
  /**
   * URL of the latest version of the document. Shown in the "you're viewing an
   * older version" warning banner. Required when `showOlderVersionWarning` is
   * true; ignored otherwise.
   */
  latestVersionUrl?: string
}

const VersionWarningText = ({
  latestVersionUrl,
}: {
  latestVersionUrl: string
}) => (
  <Banner type="warning" showIcon={false}>
    <WarningText>
      You’re viewing an older version. Comments will be added to the{' '}
      <Link to={latestVersionUrl} variant="secondary" isUnderlined>
        latest version
      </Link>
      .
    </WarningText>
  </Banner>
)

const AnnotationCommentBox = ({
  showOlderVersionWarning,
  latestVersionUrl,
  ...props
}: AnnotationCommentBoxProps) => {
  const isPreview = useIsAnnotationPreview()
  const previewRouteMatch = useRouteMatch<{ previewID?: string }>(
    routes.PREVIEW_VIEW.template()
  )
  const previewIdentifier = previewRouteMatch?.params.previewID
  const previewPassword = usePreviewPwd(previewIdentifier || '')
  const mentionableUsersSource =
    isPreview && previewIdentifier
      ? { previewIdentifier, previewPassword }
      : undefined

  const banner =
    showOlderVersionWarning && latestVersionUrl ? (
      <VersionWarningText latestVersionUrl={latestVersionUrl} />
    ) : undefined

  return (
    <StickyCommentInput
      {...props}
      banner={banner}
      mentionableUsersSource={mentionableUsersSource}
    />
  )
}

export default AnnotationCommentBox
